import { PrimaryButton, SecondaryButton } from '@/components/buttons';
import { Loading } from '@/components/loading';
import { Body3 } from '@/components/typography';
import { MultiSelect } from '@/components/ui/multi-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { UserGroupNames } from '@/modules/Permissions';
import { routes } from '@/modules/Router';
import { useChangeUser, useFetchUser, useFetchUserFormOptions } from '@/modules/Users/Users.api';
import { userManagementSchema } from '@/modules/Users/Users.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { Input } from '../ui/input';

export function EditUserForm({ userId }: { userId?: string }) {
  const { handleSubmit, control, reset, watch, setValue, ...other } = useForm<z.infer<typeof userManagementSchema>>({
    resolver: zodResolver(userManagementSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      user_type: UserGroupNames.AGRI_ADMIN,
      client_associations: [],
      project_associations: [],
      farm_associations: [],
    },
  });

  const { data: userData, status: userDataStatus } = useFetchUser(userId);
  const { data: formOptions, status: formOptionsStatus } = useFetchUserFormOptions();

  const mutation = useChangeUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [projectFilter, setProjectFilter] = useState<string | null>(null);
  const [farmFilter, setFarmFilter] = useState<string[]>([]);

  const selectedUserType = watch('user_type');
  const watchedClients = useWatch({ control, name: 'client_associations' });
  const watchedProjects = useWatch({ control, name: 'project_associations' });

  useEffect(() => {
    // If the user data is successfully fetched, set the starting values for the form
    if (userDataStatus === 'success' && formOptionsStatus === 'success') {
      setProjectFilter(userData.client_cascades[0] ?? null);
      setFarmFilter(userData.project_cascades);
      reset({
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        user_type: userData.user_type as z.infer<typeof userManagementSchema>['user_type'],
        client_associations: userData.client_associations ?? [],
        project_associations: userData.project_associations ?? [],
        farm_associations: userData.farm_associations ?? [],
      });
    }
  }, [userData, userDataStatus, formOptionsStatus, reset]);

  const projectsEditable = !(userData && selectedUserType === UserGroupNames.FARMER);
  const customerEditable = !userData;

  useEffect(() => {
    if (!customerEditable) return; // Don't cascade-clear associations when editing an existing user
    if (!watchedClients || !watchedClients.length) return;
    setValue('project_associations', []);
    setValue('farm_associations', []);
    setProjectFilter(watchedClients[0] || '');
    setFarmFilter([]);
  }, [watchedClients, setValue, customerEditable]);

  useEffect(() => {
    if (!projectsEditable) return; // Don't cascade-clear farm associations when editing an existing Farmer
    if (!watchedProjects || !watchedProjects.length) return;
    setValue('farm_associations', []);
    setFarmFilter(watchedProjects);
  }, [watchedProjects, setValue, projectsEditable]);

  const formIsLoading = formOptionsStatus === 'pending' && userDataStatus === 'pending';

  function onSubmit(data: z.infer<typeof userManagementSchema>) {
    // Remove from the submission data the Selects that were just used for filtering the selections
    if (selectedUserType === UserGroupNames.AGRI_ADMIN || selectedUserType === UserGroupNames.AGRI_QA) {
      delete data.client_associations;
      delete data.project_associations;
      delete data.farm_associations;
    } else if (selectedUserType === UserGroupNames.CLIENT_ADMIN) {
      delete data.project_associations;
      delete data.farm_associations;
    } else if (selectedUserType === UserGroupNames.PROJECT_MANAGER || selectedUserType === UserGroupNames.AGRI_PM) {
      delete data.client_associations;
      delete data.farm_associations;
    } else if (selectedUserType === UserGroupNames.FARMER) {
      delete data.client_associations;
      delete data.project_associations;
    }

    mutation.mutate(
      { userId, data },
      {
        onSuccess: async () => {
          toast.success('User updated successfully', {
            autoClose: 1000,
          });

          await queryClient.invalidateQueries({ queryKey: ['userList'] });

          navigate(routes.users);
        },
        onError: (error: Error) => {
          toast.error(error.message, {
            autoClose: 3000,
          });
        },
      }
    );
  }

  if (formOptionsStatus === 'error' || userDataStatus === 'error') {
    toast.error('Failed to fetch form data');
    return;
  }

  const isLoading = !!userId && userDataStatus === 'pending';
  const isSubmitting = mutation.isPending;

  const userTypeSelects = (formOptions?.user_types || [])
    .sort((a, b) => {
      const userGroupNameValues = Object.values(UserGroupNames) as string[];
      const aIndex = userGroupNameValues.indexOf(a);
      const bIndex = userGroupNameValues.indexOf(b);
      return aIndex - bIndex;
    })
    .map((user_type) => (
      <SelectItem key={user_type} value={user_type}>
        {user_type}
      </SelectItem>
    ));

  const customerSelects = (formOptions?.clients || []).map((client) => (
    <SelectItem key={client} value={client}>
      {client}
    </SelectItem>
  ));

  const projectOptions = (formOptions?.projects || [])
    .filter((project) => projectFilter === null || (projectFilter !== '' && project.client_id === projectFilter))
    .map((project) => ({
      label: project.project_id,
      value: project.project_id,
    }));

  const farmOptions = (formOptions?.farms || [])
    .filter((farm) => !farmFilter.length || farmFilter.includes(farm.project_id))
    .map((farm) => ({
      label: farm.farm_id,
      value: farm.farm_id,
    }));

  const projectsDisabled = watchedClients?.length === 0 && customerEditable;
  const farmsDisabled = watchedProjects?.length === 0 && projectsEditable;

  return (
    <div className={cn('w-full h-full bg-secondary-15 items-center p-16 place-items-center mb-8')}>
      {formIsLoading ? (
        <Loading />
      ) : (
        <Form handleSubmit={handleSubmit} control={control} reset={reset} watch={watch} setValue={setValue} {...other}>
          <form onSubmit={handleSubmit(onSubmit)} className={cn('flex flex-col min-w-[450px] space-y-8')}>
            <div>
              <h2 className={cn('text-lg font-semibold text-gray-700')}>{userId ? 'Edit User' : 'Add User'}</h2>
              <Body3 className="text-neutral-500">{userId ? 'Edit' : 'Add'} the user&#39;s details using the fields below.</Body3>
            </div>
            <FormField
              control={control}
              name="first_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input disabled={isLoading} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="last_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input disabled={isLoading} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input disabled={!!userId} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="user_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>User Type</FormLabel>
                  {!userId ? (
                    <Select disabled={isLoading || formOptionsStatus === 'pending'} onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a user type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>{userTypeSelects}</SelectContent>
                    </Select>
                  ) : (
                    <Input disabled={true} value={field.value} />
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            {selectedUserType !== UserGroupNames.AGRI_ADMIN && selectedUserType !== UserGroupNames.AGRI_QA && (
              <>
                <FormField
                  control={control}
                  name="client_associations"
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <FormLabel>Customer</FormLabel>
                        <FormControl>
                          {!customerEditable ? (
                            <Input disabled={true} value={userData.client_cascades.join(',')} />
                          ) : (
                            <Select
                              disabled={formOptionsStatus === 'pending'}
                              onValueChange={(val) => field.onChange(val ? [val] : [])}
                              value={field.value?.[0] ?? ''}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select a customer" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>{customerSelects}</SelectContent>
                            </Select>
                          )}
                        </FormControl>
                        <FormDescription>
                          {userData
                            ? ''
                            : selectedUserType === UserGroupNames.CLIENT_ADMIN
                              ? 'Select a customer to give access to all related projects and farms.'
                              : 'Select a customer to filter down the available projects.'}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
                {(selectedUserType === UserGroupNames.PROJECT_MANAGER ||
                  selectedUserType === UserGroupNames.AGRI_PM ||
                  selectedUserType === UserGroupNames.FARMER) && (
                  <FormField
                    control={control}
                    name="project_associations"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Projects</FormLabel>
                        <FormControl>
                          {!projectsEditable ? (
                            <Input disabled={true} value={userData.project_cascades.join(',')} />
                          ) : (
                            <MultiSelect
                              options={projectOptions || []}
                              onValueChange={field.onChange}
                              value={field.value ?? []}
                              placeholder={projectsDisabled ? 'Awaiting customer selection' : 'Select projects'}
                              disabled={projectsDisabled || formOptionsStatus === 'pending'}
                              animation={0}
                              maxCount={3}
                              variant={'default'}
                            />
                          )}
                        </FormControl>
                        <FormDescription>
                          {!projectsEditable
                            ? ''
                            : selectedUserType === UserGroupNames.PROJECT_MANAGER || selectedUserType === UserGroupNames.AGRI_PM
                              ? 'Select projects to give access to all related farms.'
                              : 'Select projects to filter down the available farms.'}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                {selectedUserType === UserGroupNames.FARMER && (
                  <FormField
                    control={control}
                    name="farm_associations"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Farms</FormLabel>
                        <FormControl>
                          <MultiSelect
                            options={farmOptions || []}
                            onValueChange={field.onChange}
                            value={field.value ?? []}
                            placeholder={farmsDisabled ? 'Awaiting project selection' : 'Select farms'}
                            disabled={farmsDisabled || formOptionsStatus === 'pending'}
                            animation={0}
                            maxCount={3}
                            variant={'default'}
                          />
                        </FormControl>
                        <FormDescription>Select farms to give specific access to these farms.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </>
            )}

            <div className={cn('flex flex-row gap-4')}>
              <Link className={cn('w-1/2')} to={routes.users}>
                <SecondaryButton>Cancel</SecondaryButton>
              </Link>
              <PrimaryButton className={cn('w-1/2')} type="submit" disabled={isLoading || isSubmitting}>
                {isSubmitting ? 'Saving…' : 'Save'}
              </PrimaryButton>
            </div>
          </form>
        </Form>
      )}
    </div>
  );
}
